import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Alert,
  Skeleton,
  Tabs,
  Tab,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Stack,
  Snackbar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Inventory2 as InventoryIcon,
  Add as AddIcon,
  Receipt as ReceiptIcon,
  Warning as WarningIcon,
  AccountTree as GenealogyIcon,
  TrackChanges as TraceIcon,
} from '@mui/icons-material';
import LotGenealogy from './LotGenealogy';

// ─── Constants ───────────────────────────────────────────────────────────────

const API_BASE =
  (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1')
    .replace(/\/api\/v\d+$/, '') + '/api/v1';

// ─── Types ───────────────────────────────────────────────────────────────────

type LotStatus = 'available' | 'quarantine' | 'expired' | 'consumed' | 'rejected' | 'pending' | 'on_hold' | 'released';

interface MaterialLot {
  id: string;
  lotNumber: string;
  materialDefinitionId: string;
  quantityValue: number;
  quantityUom: string;
  status: LotStatus;
  expiryDate?: string;
  productionDate?: string;
  supplierName?: string;
  supplierLotNumber?: string;
  batchNumber?: string;
  // nested relation that may be included
  materialDefinition?: { name?: string; itemCode?: string };
  [key: string]: any;
}

type TxType = 'receipt' | 'issue' | 'transfer' | 'adjustment' | 'scrap' | 'return';

interface InventoryTransaction {
  id: string;
  transactionType: TxType;
  materialDefinitionId: string;
  materialCode: string;
  quantityValue: number;
  quantityUom: string;
  fromLocationId?: string;
  toLocationId?: string;
  workOrderId?: string;
  performedBy?: string;
  lotNumber?: string;
  reason?: string;
  timestamp: string;
  createdAt: string;
  [key: string]: any;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function apiFetch(path: string, options?: RequestInit) {
  const token = localStorage.getItem('token');
  const resp = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers ?? {}),
    },
  });
  if (!resp.ok) {
    let msg = `HTTP ${resp.status}`;
    try {
      const body = await resp.json();
      msg = body?.message || body?.error || msg;
    } catch {
      // ignore parse errors
    }
    throw { message: msg };
  }
  return resp.json();
}

function daysUntil(dateStr: string | undefined): number | null {
  if (!dateStr) return null;
  const diff = new Date(dateStr).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

// ─── Chip colour helpers ──────────────────────────────────────────────────────

function lotStatusChip(status: LotStatus) {
  const map: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' | 'info' }> = {
    available:  { label: 'Available',  color: 'success' },
    released:   { label: 'Released',   color: 'success' },
    quarantine: { label: 'Quarantine', color: 'warning' },
    on_hold:    { label: 'On Hold',    color: 'warning' },
    pending:    { label: 'Pending',    color: 'info'    },
    expired:    { label: 'Expired',    color: 'error'   },
    consumed:   { label: 'Consumed',   color: 'default' },
    rejected:   { label: 'Rejected',   color: 'error'   },
  };
  const cfg = map[status] ?? { label: status, color: 'default' as const };
  return <Chip label={cfg.label} color={cfg.color} size="small" />;
}

function txTypeChip(type: TxType) {
  const map: Record<TxType, { label: string; color: 'success' | 'primary' | 'error' | 'default' | 'warning' }> = {
    receipt:    { label: 'Receipt',    color: 'success' },
    issue:      { label: 'Issue',      color: 'primary' },
    scrap:      { label: 'Scrap',      color: 'error'   },
    transfer:   { label: 'Transfer',   color: 'default' },
    adjustment: { label: 'Adjustment', color: 'warning' },
    return:     { label: 'Return',     color: 'default' },
  };
  const cfg = map[type] ?? { label: type, color: 'default' as const };
  return <Chip label={cfg.label} color={cfg.color} size="small" />;
}

// ─── Empty State ──────────────────────────────────────────────────────────────

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <Alert severity="info" sx={{ mt: 2 }}>{message}</Alert>
);

// ─── Tab 1: Stock Overview ────────────────────────────────────────────────────

const LOT_STATUSES: LotStatus[] = ['available', 'quarantine', 'expired', 'consumed', 'rejected', 'pending', 'on_hold', 'released'];

interface ReceiveLotForm {
  materialDefinitionId: string;
  lotNumber: string;
  quantityValue: string;
  quantityUom: string;
  expiryDate: string;
  supplierName: string;
  status: LotStatus;
}

const defaultLotForm = (): ReceiveLotForm => ({
  materialDefinitionId: '',
  lotNumber: '',
  quantityValue: '',
  quantityUom: 'kg',
  expiryDate: '',
  supplierName: '',
  status: 'available',
});

interface StockOverviewProps {
  onTrace: (lotNumber: string) => void;
}

interface SnackState {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
}

const StockOverview: React.FC<StockOverviewProps> = ({ onTrace }) => {
  const [lots, setLots] = useState<MaterialLot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<ReceiveLotForm>(defaultLotForm());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Status transition dialogs
  const [quarantineDialog, setQuarantineDialog] = useState<{ open: boolean; lot: MaterialLot | null }>({ open: false, lot: null });
  const [rejectDialog, setRejectDialog] = useState<{ open: boolean; lot: MaterialLot | null; reason: string }>({ open: false, lot: null, reason: '' });
  const [statusUpdating, setStatusUpdating] = useState<string | null>(null); // lotId being updated
  const [snack, setSnack] = useState<SnackState>({ open: false, message: '', severity: 'success' });

  const fetchLots = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : '';
      const resp = await apiFetch(`/master-data/material-lots${qs}`);
      const data = resp.data ?? resp;
      setLots(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e.message || 'Failed to load material lots');
      setLots([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchLots(); }, [fetchLots]);

  const openDialog = () => {
    setForm(defaultLotForm());
    setSubmitError(null);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.materialDefinitionId || !form.lotNumber || !form.quantityValue) {
      setSubmitError('Material ID, Lot Number and Quantity are required.');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await apiFetch('/master-data/material-lots', {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          quantityValue: parseFloat(form.quantityValue),
          expiryDate: form.expiryDate || undefined,
          supplierName: form.supplierName || undefined,
        }),
      });
      setDialogOpen(false);
      fetchLots();
    } catch (e: any) {
      setSubmitError(e.message || 'Failed to receive lot');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Status transition helpers ───────────────────────────────────────────────

  const updateStatus = async (lotId: string, payload: Record<string, string>) => {
    setStatusUpdating(lotId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/master-data/material-lots/${lotId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const body = await res.json();
          msg = body?.message || body?.error || msg;
        } catch {
          // ignore
        }
        throw { message: msg };
      }
      const newStatus = payload.status;
      setSnack({ open: true, message: `Lot status updated to "${newStatus}"`, severity: 'success' });
      await fetchLots();
    } catch (e: any) {
      setSnack({ open: true, message: e.message || 'Failed to update lot status', severity: 'error' });
    } finally {
      setStatusUpdating(null);
    }
  };

  const handleRelease = (lot: MaterialLot) => {
    updateStatus(lot.id, { status: 'released' });
  };

  const handleQuarantineConfirm = () => {
    if (!quarantineDialog.lot) return;
    setQuarantineDialog({ open: false, lot: null });
    updateStatus(quarantineDialog.lot.id, { status: 'quarantine' });
  };

  const handleRejectConfirm = () => {
    if (!rejectDialog.lot) return;
    if (rejectDialog.reason.trim().length < 10) return;
    const lot = rejectDialog.lot;
    const reason = rejectDialog.reason.trim();
    setRejectDialog({ open: false, lot: null, reason: '' });
    updateStatus(lot.id, { status: 'rejected', rejectionReason: reason });
  };

  // Render action buttons based on current lot status
  const renderActions = (lot: MaterialLot) => {
    const isUpdating = statusUpdating === lot.id;

    if (lot.status === 'rejected' || lot.status === 'expired' || lot.status === 'consumed') {
      return (
        <Chip
          label={lot.status === 'rejected' ? 'Rejected' : lot.status === 'expired' ? 'Expired' : 'Consumed'}
          size="small"
          sx={{ color: 'text.secondary', borderColor: 'grey.400' }}
          variant="outlined"
        />
      );
    }

    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap">
        {/* Release: available for pending, on_hold, quarantine */}
        {(lot.status === 'pending' || lot.status === 'on_hold' || lot.status === 'quarantine') && (
          <Button
            size="small"
            variant="outlined"
            color="success"
            disabled={isUpdating}
            onClick={() => handleRelease(lot)}
            startIcon={isUpdating ? <CircularProgress size={12} /> : undefined}
          >
            Release
          </Button>
        )}

        {/* Quarantine: available for released or available */}
        {(lot.status === 'released' || lot.status === 'available') && (
          <Button
            size="small"
            variant="outlined"
            color="warning"
            disabled={isUpdating}
            onClick={() => setQuarantineDialog({ open: true, lot })}
          >
            Quarantine
          </Button>
        )}

        {/* Reject: available for quarantine */}
        {lot.status === 'quarantine' && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={isUpdating}
            onClick={() => setRejectDialog({ open: true, lot, reason: '' })}
          >
            Reject
          </Button>
        )}
      </Stack>
    );
  };

  const displayed = lots;

  return (
    <Box>
      {/* Toolbar */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Filter by Status</InputLabel>
          <Select
            value={statusFilter}
            label="Filter by Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">All Statuses</MenuItem>
            {LOT_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1).replace('_', ' ')}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openDialog}
        >
          Receive Lot
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Stack spacing={1}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
          ))}
        </Stack>
      ) : displayed.length === 0 ? (
        <EmptyState message="No material lots found. Use 'Receive Lot' to add stock." />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.50' }}>
                <TableCell>Lot Number</TableCell>
                <TableCell>Material</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell>UoM</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Expiry Date</TableCell>
                <TableCell>Location / Supplier</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>Trace</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayed.map((lot) => (
                <TableRow key={lot.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">{lot.lotNumber}</Typography>
                  </TableCell>
                  <TableCell>
                    {lot.materialDefinition?.name ?? lot.materialDefinition?.itemCode ?? lot.materialDefinitionId}
                  </TableCell>
                  <TableCell align="right">{Number(lot.quantityValue).toLocaleString()}</TableCell>
                  <TableCell>{lot.quantityUom}</TableCell>
                  <TableCell>{lotStatusChip(lot.status)}</TableCell>
                  <TableCell>
                    {lot.expiryDate
                      ? new Date(lot.expiryDate).toLocaleDateString()
                      : '—'}
                  </TableCell>
                  <TableCell>{lot.supplierName || '—'}</TableCell>
                  <TableCell>{renderActions(lot)}</TableCell>
                  <TableCell>
                    <Tooltip title="Trace this lot">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => onTrace(lot.lotNumber)}
                      >
                        <TraceIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Receive Lot Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Receive Lot</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {submitError && <Alert severity="error">{submitError}</Alert>}
            <TextField
              label="Material Definition ID"
              fullWidth
              required
              value={form.materialDefinitionId}
              onChange={(e) => setForm({ ...form, materialDefinitionId: e.target.value })}
              placeholder="UUID of material item"
            />
            <TextField
              label="Lot Number"
              fullWidth
              required
              value={form.lotNumber}
              onChange={(e) => setForm({ ...form, lotNumber: e.target.value })}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Quantity"
                type="number"
                required
                fullWidth
                value={form.quantityValue}
                onChange={(e) => setForm({ ...form, quantityValue: e.target.value })}
                inputProps={{ min: 0, step: 'any' }}
              />
              <TextField
                label="Unit of Measure"
                fullWidth
                value={form.quantityUom}
                onChange={(e) => setForm({ ...form, quantityUom: e.target.value })}
                placeholder="e.g. kg"
              />
            </Stack>
            <TextField
              label="Expiry Date"
              type="date"
              fullWidth
              value={form.expiryDate}
              onChange={(e) => setForm({ ...form, expiryDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Supplier Name"
              fullWidth
              value={form.supplierName}
              onChange={(e) => setForm({ ...form, supplierName: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={form.status}
                label="Status"
                onChange={(e) => setForm({ ...form, status: e.target.value as LotStatus })}
              >
                {LOT_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1).replace('_', ' ')}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Saving...' : 'Receive'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quarantine Confirmation Dialog */}
      <Dialog open={quarantineDialog.open} onClose={() => setQuarantineDialog({ open: false, lot: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Move to Quarantine</DialogTitle>
        <DialogContent>
          <Typography>
            Move lot <strong>{quarantineDialog.lot?.lotNumber}</strong> to quarantine?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            The lot will be placed on hold until it is released or rejected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuarantineDialog({ open: false, lot: null })}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleQuarantineConfirm}>
            Move to Quarantine
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, lot: null, reason: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Lot</DialogTitle>
        <DialogContent>
          <Typography mb={2}>
            Reject lot <strong>{rejectDialog.lot?.lotNumber}</strong>. This action cannot be undone.
          </Typography>
          <TextField
            label="Rejection Reason"
            fullWidth
            multiline
            rows={3}
            required
            value={rejectDialog.reason}
            onChange={(e) => setRejectDialog({ ...rejectDialog, reason: e.target.value })}
            helperText={
              rejectDialog.reason.trim().length > 0 && rejectDialog.reason.trim().length < 10
                ? 'Reason must be at least 10 characters'
                : ''
            }
            error={rejectDialog.reason.trim().length > 0 && rejectDialog.reason.trim().length < 10}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, lot: null, reason: '' })}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectConfirm}
            disabled={rejectDialog.reason.trim().length < 10}
          >
            Confirm Rejection
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar feedback */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ─── Tab 2: Transactions ──────────────────────────────────────────────────────

const TX_TYPES: TxType[] = ['receipt', 'issue', 'transfer', 'adjustment', 'scrap', 'return'];

interface RecordTxForm {
  transactionType: TxType;
  materialDefinitionId: string;
  materialCode: string;
  quantityValue: string;
  quantityUom: string;
  fromLocationId: string;
  toLocationId: string;
  workOrderId: string;
  performedBy: string;
  lotNumber: string;
  reason: string;
}

const defaultTxForm = (): RecordTxForm => ({
  transactionType: 'receipt',
  materialDefinitionId: '',
  materialCode: '',
  quantityValue: '',
  quantityUom: 'kg',
  fromLocationId: '',
  toLocationId: '',
  workOrderId: '',
  performedBy: '',
  lotNumber: '',
  reason: '',
});

const Transactions: React.FC = () => {
  const [transactions, setTransactions] = useState<InventoryTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [materialFilter, setMaterialFilter] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<RecordTxForm>(defaultTxForm());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (typeFilter) params.set('transactionType', typeFilter);
      if (dateFrom) params.set('from', new Date(dateFrom).toISOString());
      if (dateTo) params.set('to', new Date(dateTo + 'T23:59:59').toISOString());
      if (materialFilter) params.set('itemCode', materialFilter.trim());
      const qs = params.toString() ? `?${params.toString()}` : '';
      const resp = await apiFetch(`/work-orders/inventory-transactions${qs}`);
      const data = resp.data ?? resp;
      setTransactions(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e.message || 'Failed to load inventory transactions');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, dateFrom, dateTo, materialFilter]);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  const clearFilters = () => {
    setTypeFilter('');
    setDateFrom('');
    setDateTo('');
    setMaterialFilter('');
  };

  const hasFilters = typeFilter || dateFrom || dateTo || materialFilter;

  const openDialog = () => {
    setForm(defaultTxForm());
    setSubmitError(null);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.materialDefinitionId || !form.materialCode || !form.quantityValue) {
      setSubmitError('Material ID, Material Code and Quantity are required.');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await apiFetch('/work-orders/inventory-transactions', {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          quantityValue: parseFloat(form.quantityValue),
          fromLocationId: form.fromLocationId || undefined,
          toLocationId: form.toLocationId || undefined,
          workOrderId: form.workOrderId || undefined,
          performedBy: form.performedBy || undefined,
          lotNumber: form.lotNumber || undefined,
          reason: form.reason || undefined,
        }),
      });
      setDialogOpen(false);
      fetchTransactions();
    } catch (e: any) {
      setSubmitError(e.message || 'Failed to record transaction');
    } finally {
      setSubmitting(false);
    }
  };

  // Client-side material filter fallback (if server doesn't support itemCode param)
  const displayed = materialFilter
    ? transactions.filter((tx) => {
        const q = materialFilter.toLowerCase();
        return (
          (tx.materialCode ?? '').toLowerCase().includes(q) ||
          (tx.materialDefinitionId ?? '').toLowerCase().includes(q)
        );
      })
    : transactions;

  return (
    <Box>
      {/* Filters toolbar */}
      <Box display="flex" gap={2} alignItems="center" mb={2} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Filter by Type</InputLabel>
          <Select
            value={typeFilter}
            label="Filter by Type"
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <MenuItem value="">All Types</MenuItem>
            {TX_TYPES.map((t) => (
              <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="From Date"
          type="date"
          size="small"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="To Date"
          type="date"
          size="small"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Material (code or name)"
          size="small"
          value={materialFilter}
          onChange={(e) => setMaterialFilter(e.target.value)}
          placeholder="e.g. FLOUR-001"
          sx={{ minWidth: 200 }}
        />
        {hasFilters && (
          <Button size="small" variant="outlined" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
        <Box flexGrow={1} />
        <Button
          variant="contained"
          startIcon={<ReceiptIcon />}
          onClick={openDialog}
        >
          Record Transaction
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Stack spacing={1}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
          ))}
        </Stack>
      ) : displayed.length === 0 ? (
        <EmptyState message="No inventory transactions found for the selected filters." />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.50' }}>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Material</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell>From Location</TableCell>
                <TableCell>To Location</TableCell>
                <TableCell>Work Order</TableCell>
                <TableCell>Performed By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayed.map((tx) => (
                <TableRow key={tx.id} hover>
                  <TableCell>
                    {new Date(tx.timestamp ?? tx.createdAt).toLocaleString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </TableCell>
                  <TableCell>{txTypeChip(tx.transactionType)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {tx.materialCode}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {Number(tx.quantityValue).toLocaleString()} {tx.quantityUom}
                  </TableCell>
                  <TableCell>{tx.fromLocationId || '—'}</TableCell>
                  <TableCell>{tx.toLocationId || '—'}</TableCell>
                  <TableCell>{tx.workOrderId || '—'}</TableCell>
                  <TableCell>{tx.performedBy || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Record Transaction Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Inventory Transaction</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {submitError && <Alert severity="error">{submitError}</Alert>}
            <FormControl fullWidth required>
              <InputLabel>Transaction Type</InputLabel>
              <Select
                value={form.transactionType}
                label="Transaction Type"
                onChange={(e) => setForm({ ...form, transactionType: e.target.value as TxType })}
              >
                {TX_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Material Definition ID"
              fullWidth
              required
              value={form.materialDefinitionId}
              onChange={(e) => setForm({ ...form, materialDefinitionId: e.target.value })}
              placeholder="UUID"
            />
            <TextField
              label="Material Code"
              fullWidth
              required
              value={form.materialCode}
              onChange={(e) => setForm({ ...form, materialCode: e.target.value })}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Quantity"
                type="number"
                required
                fullWidth
                value={form.quantityValue}
                onChange={(e) => setForm({ ...form, quantityValue: e.target.value })}
                inputProps={{ min: 0, step: 'any' }}
              />
              <TextField
                label="UoM"
                fullWidth
                value={form.quantityUom}
                onChange={(e) => setForm({ ...form, quantityUom: e.target.value })}
                placeholder="kg"
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="From Location ID"
                fullWidth
                value={form.fromLocationId}
                onChange={(e) => setForm({ ...form, fromLocationId: e.target.value })}
              />
              <TextField
                label="To Location ID"
                fullWidth
                value={form.toLocationId}
                onChange={(e) => setForm({ ...form, toLocationId: e.target.value })}
              />
            </Stack>
            <TextField
              label="Work Order ID"
              fullWidth
              value={form.workOrderId}
              onChange={(e) => setForm({ ...form, workOrderId: e.target.value })}
            />
            <TextField
              label="Lot Number"
              fullWidth
              value={form.lotNumber}
              onChange={(e) => setForm({ ...form, lotNumber: e.target.value })}
            />
            <TextField
              label="Performed By"
              fullWidth
              value={form.performedBy}
              onChange={(e) => setForm({ ...form, performedBy: e.target.value })}
            />
            <TextField
              label="Reason / Notes"
              fullWidth
              multiline
              rows={2}
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Saving...' : 'Record'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── Tab 3: Expiry Alerts ─────────────────────────────────────────────────────

const EXPIRY_WINDOW_DAYS = 30;

const ExpiryAlerts: React.FC = () => {
  const [lots, setLots] = useState<MaterialLot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchExpiring = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch all available lots (not already consumed/expired by status)
        const resp = await apiFetch('/master-data/material-lots');
        const data: MaterialLot[] = resp.data ?? (Array.isArray(resp) ? resp : []);

        const soon = data
          .filter((lot) => {
            if (!lot.expiryDate) return false;
            const days = daysUntil(lot.expiryDate);
            return days !== null && days <= EXPIRY_WINDOW_DAYS;
          })
          .sort((a, b) => new Date(a.expiryDate!).getTime() - new Date(b.expiryDate!).getTime());

        setLots(soon);
      } catch (e: any) {
        setError(e.message || 'Failed to load expiry data');
        setLots([]);
      } finally {
        setLoading(false);
      }
    };

    fetchExpiring();
  }, []);

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Stack spacing={1}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
          ))}
        </Stack>
      ) : lots.length === 0 ? (
        <Alert severity="success" sx={{ mt: 2 }}>
          No lots expiring within the next {EXPIRY_WINDOW_DAYS} days.
        </Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'error.50' }}>
                <TableCell>Lot Number</TableCell>
                <TableCell>Material</TableCell>
                <TableCell>Expiry Date</TableCell>
                <TableCell align="right">Days Remaining</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lots.map((lot) => {
                const days = daysUntil(lot.expiryDate);
                const overdue = days !== null && days < 0;
                return (
                  <TableRow key={lot.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">{lot.lotNumber}</Typography>
                    </TableCell>
                    <TableCell>
                      {lot.materialDefinition?.name ?? lot.materialDefinition?.itemCode ?? lot.materialDefinitionId}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={new Date(lot.expiryDate!).toLocaleDateString()}
                        color={overdue ? 'error' : 'warning'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        color={overdue ? 'error.main' : days !== null && days <= 7 ? 'warning.main' : 'text.primary'}
                        fontWeight="bold"
                      >
                        {days !== null ? (overdue ? `${Math.abs(days)} overdue` : `${days} days`) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {Number(lot.quantityValue).toLocaleString()} {lot.quantityUom}
                    </TableCell>
                    <TableCell>{lotStatusChip(lot.status)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const TAB_STOCK = 0;
const TAB_TRANSACTIONS = 1;
const TAB_EXPIRY = 2;
const TAB_GENEALOGY = 3;

const InventoryManagement: React.FC = () => {
  const [tab, setTab] = useState(TAB_STOCK);
  const [genealogyLotNumber, setGenealogyLotNumber] = useState<string | undefined>(undefined);
  // We track the expiry count separately so the badge can render before the tab is opened
  const [expiryCount, setExpiryCount] = useState<number | null>(null);

  // Fetch expiry count once on mount for the badge
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const resp = await apiFetch('/master-data/material-lots');
        const data: MaterialLot[] = resp.data ?? (Array.isArray(resp) ? resp : []);
        const count = data.filter((lot) => {
          if (!lot.expiryDate) return false;
          const d = daysUntil(lot.expiryDate);
          return d !== null && d <= EXPIRY_WINDOW_DAYS;
        }).length;
        setExpiryCount(count);
      } catch {
        setExpiryCount(null);
      }
    };
    fetchCount();
  }, []);

  const handleTrace = (lotNumber: string) => {
    setGenealogyLotNumber(lotNumber);
    setTab(TAB_GENEALOGY);
  };

  return (
    <Box sx={{ p: 0 }}>
      {/* Page header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <InventoryIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h4" component="h1">
            Inventory Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Material lots, inventory transactions and expiry monitoring
          </Typography>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            label="Stock Overview"
            icon={<InventoryIcon />}
            iconPosition="start"
          />
          <Tab
            label="Transactions"
            icon={<ReceiptIcon />}
            iconPosition="start"
          />
          <Tab
            label={
              <Badge
                badgeContent={expiryCount ?? 0}
                color="error"
                invisible={!expiryCount}
              >
                <Box display="flex" alignItems="center" gap={0.5}>
                  <WarningIcon fontSize="small" />
                  Expiry Alerts
                </Box>
              </Badge>
            }
          />
          <Tab
            label="Lot Genealogy"
            icon={<GenealogyIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {tab === TAB_STOCK && <StockOverview onTrace={handleTrace} />}
      {tab === TAB_TRANSACTIONS && <Transactions />}
      {tab === TAB_EXPIRY && <ExpiryAlerts />}
      {tab === TAB_GENEALOGY && (
        <LotGenealogy initialLotNumber={genealogyLotNumber} />
      )}
    </Box>
  );
};

export default InventoryManagement;
